import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout/Layout';
import api from '../services/api';
import Toast from '../components/Toast';
import { exportToCSV } from '../utils/export';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './History.css';

function estimate1rm(weight, reps) {
  if (!weight || !reps || reps <= 0) return null;
  if (reps === 1) return weight;
  return Math.round(weight * (1 + reps / 30) * 10) / 10;
}

function getStartOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function groupByWeek(sessions, weekStart) {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);

  const prevStart = new Date(weekStart);
  prevStart.setDate(prevStart.getDate() - 7);
  const prevEnd = new Date(weekStart);
  prevEnd.setDate(prevEnd.getDate() - 1);
  prevEnd.setHours(23, 59, 59, 999);

  const groups = {
    'Esta semana': [],
    'La semana pasada': [],
    'Antes': [],
  };

  sessions.forEach(s => {
    const d = new Date(s.date);
    if (d >= weekStart && d <= weekEnd) {
      groups['Esta semana'].push(s);
    } else if (d >= prevStart && d <= prevEnd) {
      groups['La semana pasada'].push(s);
    } else {
      groups['Antes'].push(s);
    }
  });

  return groups;
}

function buildMonthlyVolume(sessions) {
  const months = {};
  sessions.forEach(s => {
    const d = new Date(s.date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!months[key]) months[key] = { month: key, volume: 0, count: 0 };
    months[key].volume += s.total_volume || 0;
    months[key].count += 1;
  });
  return Object.values(months).sort((a, b) => a.month.localeCompare(b.month)).slice(-12)
    .map(m => ({ ...m, label: m.month.substring(5), volume: Math.round(m.volume) }));
}

export default function History() {
  const [sessions,  setSessions]  = useState([]);
  const [stats,     setStats]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [expanded,  setExpanded]  = useState({});
  const [toast,     setToast]     = useState(null);
  const [showChart, setShowChart] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [editSet, setEditSet] = useState(null);
  const [editWeight, setEditWeight] = useState('');
  const [editReps, setEditReps] = useState('');
  const [savingSet, setSavingSet] = useState(false);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sessRes, statsRes] = await Promise.all([
        api.get('/workouts/history', { params: { per_page: 365 } }),
        api.get('/workouts/stats').catch(() => ({ data: null })),
      ]);
      const allSessions = sessRes.data.sessions || sessRes.data || [];
      setSessions(allSessions);
      setStats(statsRes.data);
    } catch {
      showToast('Error al cargar el historial');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function toggleExpand(id) {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
    setEditSet(null);
  }

  function startEditSet(s) {
    setEditSet(s.id);
    setEditWeight(String(s.weight || ''));
    setEditReps(String(s.reps || ''));
  }

  async function saveEditSet() {
    if (!editSet) return;
    setSavingSet(true);
    try {
      await api.put(`/workouts/sets/${editSet}`, {
        weight: parseFloat(editWeight) || 0,
        reps: parseInt(editReps, 10) || 0,
      });
      showToast('Serie actualizada', 'success');
      setEditSet(null);
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al actualizar la serie');
    } finally {
      setSavingSet(false);
    }
  }

  async function deleteSet(setId) {
    if (!window.confirm('¿Eliminar esta serie?')) return;
    try {
      await api.delete(`/workouts/sets/${setId}`);
      showToast('Serie eliminada', 'success');
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al eliminar la serie');
    }
  }

  const now = new Date();
  const baseWeekStart = getStartOfWeek(now);
  const weekStart = new Date(baseWeekStart);
  weekStart.setDate(weekStart.getDate() + weekOffset * 7);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);

  const currentWeekStart = new Date(baseWeekStart);
  const currentWeekEnd = new Date(baseWeekStart);
  currentWeekEnd.setDate(currentWeekEnd.getDate() + 6);
  currentWeekEnd.setHours(23, 59, 59, 999);

  const weekStr = `${weekStart.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' })}`;

  const visibleSessions = sessions.filter(s => {
    const d = new Date(s.date);
    return d >= weekStart && d <= weekEnd;
  });

  const groups = groupByWeek(sessions, baseWeekStart);
  const monthlyVolume = buildMonthlyVolume(sessions);
  const hasChartData = monthlyVolume.length > 1;

  const thisWeekCount = visibleSessions.length;

  return (
    <Layout>
      {toast && <Toast message={toast.msg} type={toast.type} />}
      <div className="page">
        <div className="page-header">
          <div className="page-title">Historial</div>
          {hasChartData && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowChart(s => !s)}>
              {showChart ? 'Lista' : 'Gráfico'}
            </button>
          )}
          {sessions.length > 0 && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => exportToCSV(sessions)}>
              CSV
            </button>
          )}
        </div>

        {stats && (
          <div className="stat-cards">
            <div className="stat-card">
              <div className="stat-value">{stats.total_workouts ?? 0}</div>
              <div className="stat-label">Entrenamientos</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.current_streak ?? 0}🔥</div>
              <div className="stat-label">Racha</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{thisWeekCount}</div>
              <div className="stat-label">Esta semana</div>
            </div>
          </div>
        )}

        {showChart && hasChartData && (
          <div className="chart-card card mx-16 mb-16">
            <h3 className="chart-title">Volumen mensual (kg)</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthlyVolume}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                <XAxis dataKey="label" stroke="#a0a0b0" fontSize={11} />
                <YAxis stroke="#a0a0b0" fontSize={11} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 8, color: '#eaeaea' }} formatter={(value) => [`${value.toLocaleString()} kg`, 'Volumen']} />
                <Bar dataKey="volume" fill="#e94560" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Week navigation */}
        <div className="week-nav px-16">
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setWeekOffset(o => o - 1)}>◀</button>
          <span className="text-sm fw-600">{weekStr}</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setWeekOffset(o => o + 1)}>▶</button>
        </div>

        {loading ? (
          <div className="px-16">
            <div className="skeleton skeleton-card-lg" />
            <div className="skeleton skeleton-card-lg" />
            <div className="skeleton skeleton-card" />
          </div>
        ) : visibleSessions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📊</div>
            <div className="empty-state-title">{weekOffset === 0 ? 'Sin entrenamientos esta semana' : 'Sin entrenamientos en esta fecha'}</div>
            <div className="empty-state-text">No hay entrenamientos en el rango seleccionado.</div>
          </div>
        ) : (
          <div className="px-16">
            {/* Sorted by date descending within the visible week */}
            {[...visibleSessions].sort((a, b) => new Date(b.date) - new Date(a.date)).map(session => {
              const isExp = expanded[session.id];
              const dateStr = new Date(session.date).toLocaleDateString('es-ES', {
                weekday: 'short', month: 'short', day: 'numeric',
              });
              return (
                <div key={session.id} className="session-card card mb-8">
                  <button className="session-card-header" onClick={() => toggleExpand(session.id)} type="button">
                    <div className="session-card-left">
                      <div className="session-date">{dateStr}</div>
                      <div className="session-meta text-muted text-sm">
                        {session.exercise_count ?? '?'} ejercicios · {session.total_sets ?? '?'} series
                      </div>
                    </div>
                    <span className="session-chevron">{isExp ? '▲' : '▼'}</span>
                  </button>

                  {isExp && (
                    <div className="session-detail">
                      <div className="divider" />
                      {session.notes && (
                        <div className="text-sm text-muted" style={{ padding: '8px 0', fontStyle: 'italic', borderBottom: '1px solid #2a2a4a', marginBottom: 8 }}>
                          📝 {session.notes}
                        </div>
                      )}
                      {(session.exercises || []).map((ex, i) => (
                        <div key={i} className="session-ex">
                          <div className="session-ex-name">{ex.exercise_name}</div>
                          <div className="session-sets-row">
                            {(ex.sets || []).map((s, si) => (
                              editSet === s.id ? (
                                <div key={si} className="history-set-edit-inline">
                                  {ex.category === 'cardio' ? (
                                    <input className="form-input form-input-sm" type="number" step="0.5" min="0" style={{ width: 60 }} value={editWeight} onChange={e => setEditWeight(e.target.value)} placeholder="min" />
                                  ) : ex.is_bodyweight ? (
                                    <input className="form-input form-input-sm" type="number" min="0" style={{ width: 60 }} value={editReps} onChange={e => setEditReps(e.target.value)} placeholder="reps" />
                                  ) : (
                                    <>
                                      <input className="form-input form-input-sm" type="number" step="0.5" min="0" style={{ width: 60 }} value={editWeight} onChange={e => setEditWeight(e.target.value)} placeholder="kg" />
                                      <span className="text-muted">×</span>
                                      <input className="form-input form-input-sm" type="number" min="0" style={{ width: 60 }} value={editReps} onChange={e => setEditReps(e.target.value)} placeholder="reps" />
                                    </>
                                  )}
                                  <button className="btn btn-xs btn-primary" onClick={saveEditSet} disabled={savingSet}>{savingSet ? '...' : '✓'}</button>
                                  <button className="btn btn-xs btn-secondary" onClick={() => setEditSet(null)}>✕</button>
                                </div>
                              ) : (
                                <div key={si} className="history-set-pill-wrapper">
                                  <span className="history-set-pill" style={{ cursor: 'pointer' }} onClick={() => startEditSet(s)} title="Editar">
                                    {ex.category === 'cardio' ? (
                                      <>{s.weight || s.reps || 0} min</>
                                    ) : ex.is_bodyweight ? (
                                      <>{s.reps || 0} reps</>
                                    ) : (
                                      <>{s.weight}kg × {s.reps}</>
                                    )}
                                    {ex.category !== 'cardio' && estimate1rm(s.weight, s.reps) && (
                                      <span className="text-xs text-muted" style={{ marginLeft: 4 }}>(1RM: {estimate1rm(s.weight, s.reps)})</span>
                                    )}
                                  </span>
                                  <button className="history-set-delete" onClick={() => deleteSet(s.id)} type="button" title="Eliminar serie">×</button>
                                </div>
                              )
                            ))}
                          </div>
                        </div>
                      ))}
                      {(!session.exercises || session.exercises.length === 0) && (
                        <div className="text-muted text-sm" style={{ textAlign: 'center', padding: '8px 0' }}>
                          Sin detalles disponibles
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
